import { clone } from 'rambda';
import { type TemplateElement } from '@bynder-studio/render-core';

type Element = TemplateElement & {
    properties: {
        hidden: boolean;
        locked: boolean;
        allowToggleVisibility: boolean;
        onlyAllowOneSelection?: boolean;
    };
    children?: Element[];
};

const filterElementsRecursively = <TElement extends Element>(
    elements: TElement[],
    preserveHidden = false,
    isDynamicElementSelectionEnabled = false,
): TElement[] =>
    elements.reduce<TElement[]>((acc, element) => {
        const { hidden, allowToggleVisibility, onlyAllowOneSelection } = element.properties;
        const shouldPreserveHiddenChildren =
            preserveHidden || (isDynamicElementSelectionEnabled && Boolean(onlyAllowOneSelection));

        // todo: Remove after BE implements inherited locks
        // if ((hidden && !allowToggleVisibility) || locked) {
        if (!preserveHidden && hidden && !allowToggleVisibility) {
            return acc;
        }

        if ('children' in element && element.children?.length) {
            const children = filterElementsRecursively(
                element.children as TElement[],
                shouldPreserveHiddenChildren,
                isDynamicElementSelectionEnabled,
            );

            if (!children.length) {
                return acc;
            }

            acc.push({
                ...element,
                children,
            });

            return acc;
        }

        acc.push(element);

        return acc;
    }, []);

export const getVisibleElements = <TElement extends Element>(
    pages: Array<{ elements: TElement[] }>,
    selectedPageIdx: number,
    isDynamicElementSelectionEnabled = false,
): TElement[] =>
    filterElementsRecursively(clone(pages[selectedPageIdx].elements), false, isDynamicElementSelectionEnabled);
